import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Books from '../../graphql/queries/Books';
import { Container, Box, Typography, Button } from '@mui/material';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import {serializeBooks, serializeItem} from '../../helpers/serializers/Books';
import Collection from '../Collection';
import ActionBar from "../Landing/Navigation/ActionBar";
import { useTranslation } from "react-i18next";

const categoryNames = t => {
    return {
        "full-collection": t('allBooks', 'Усі книги'),
        "kids": t('kidsBooks', 'Дитяча література'),
        "adult": t('adultBooks', 'Доросла література'),
        "english": t('englishBooks', 'Англомовна література'),
        "my-holdings": t('myHoldings', 'Мої примірники'),
        "my-reservations": t('myReservations', 'Мої бронювання'),
        "my-readings": t('myReadings', 'Мої читання')
    }
};

const Catalogue = () => {
    const { category } = useParams(); // Get category from URL
    const normalizedCategory = category === "full-collection" ? "all" : category; // Convert for API
    const { t } = useTranslation();

    const [data, setData] = useState([]);
    const [pageInfo, setPageInfo] = useState(null); // Pagination info
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ tags: [] });
    const [sort, setSort] = useState(null);
    const [showNewIncomings, setShowNewIncomings] = useState(false);

    const canShowNewIncomings = category === "full-collection";

    const loadBooks = (after = null) => {
        setLoading(true);

        // Adjust filters based on "New Incomings" toggle
        const adjustedFilter = {
            ...filters,
            category: showNewIncomings && canShowNewIncomings ? undefined : normalizedCategory, // Remove category if showing new incomings
            newIncomings: showNewIncomings === true && canShowNewIncomings, // Filter for new items if toggled
            tags: filters.tags.length > 0 ? filters.tags.map(tag => tag.toLowerCase()) : undefined,
        };

        Books.fetch({
            first: showNewIncomings ? 20 : 12, // Fetch 20 if "New Incomings" is active
            after,
            filter: adjustedFilter,
            sort: showNewIncomings ? { field: "created_at", order: "DESC" } : sort, // Sort by latest if "New Incomings"
        })
            .then((response) => {
                const newEdges = response.books.edges;
                const serializedData = serializeBooks(newEdges);

                setData(after ? [...data, ...serializedData] : serializedData);
                setPageInfo(response.books.pageInfo);
            })
            .catch((error) => {
                console.error('Error fetching books:', error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const updateBook = (updatedEntity) => {
        setData(prevData =>
            prevData.map(book => {
                if (!book.entities) return book;

                const entityIndex = book.entities.findIndex(entity => entity.id === updatedEntity.id);
                if (entityIndex === -1) return book;

                const updatedEntities = [...book.entities];
                updatedEntities[entityIndex] = updatedEntity;

                const updatedBook = { ...book, entities: updatedEntities };
                return serializeItem(updatedBook);
            })
        );
    };

    useEffect(() => {
        // Fetch data when filters, sorting, category, or "New Incomings" changes
        loadBooks();
    }, [filters, sort, category, showNewIncomings, canShowNewIncomings]);

    const handleSearch = (query) => {
        setData([]);
        setFilters((prevFilters) => ({ ...prevFilters, term: query }));
    };

    const handleFilter = (filter) => {
        setData([]);
        setFilters((prevFilters) => ({
            ...prevFilters,
            availability: filter === 'available' ? true : filter === 'unavailable' ? false : undefined,
        }));
    };

    const handleTagFilter = (selectedTags) => {
        setData([]);
        setFilters((prevFilters) => ({ ...prevFilters, tags: selectedTags.slice(0, 3) }));
    };

    const handleSort = (sortField) => {
        setData([]);
        setSort({ field: sortField, order: 'ASC' });
    };

    const handleLoadMore = () => {
        if (pageInfo?.hasNextPage) {
            loadBooks(pageInfo.endCursor);
        }
    };

    const resetFilters = () => {
        setFilters({ tags: [] });
        setSort(null);
        setShowNewIncomings(false);
        setData([]);
    };

    return (
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
            <Box mt={2}>
                <ActionBar
                    onSearch={handleSearch}
                    onFilter={handleFilter}
                    onTagFilter={handleTagFilter}
                    onSort={handleSort}
                />
            </Box>
            <Box p={{ xs: 1, sm: 2 }}>
                {!loading && data.length === 0 ? (
                    <Box textAlign="center" mt={6} mb={6} sx={{ color: 'text.secondary' }}>
                        <SearchOffOutlinedIcon sx={{ fontSize: 64, mb: 1, opacity: 0.6 }} />
                        <Typography variant="h6">{t('noResults', 'Нічого не знайдено')}</Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {t('tryAdjustFilters', 'Спробуйте змінити фільтри або пошук')}
                        </Typography>
                        <Button variant="outlined" sx={{ mt: 2 }} onClick={resetFilters}>
                            {t('resetFilters', 'Скинути фільтри')}
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Collection
                            onUpdateEntity={updateBook}
                            loading={loading}
                            skeletonCount={showNewIncomings ? 20 : 12}
                            title={showNewIncomings && canShowNewIncomings ? t('newIncomings', 'Нові надходження') : (categoryNames(t)[category] || t('library', 'Бібліотека'))}
                            items={data}
                            sx={{ mb: 4 }}
                        >
                            {canShowNewIncomings && (
                                <Box display="flex" justifyContent="flex-end" mt={2} mb={1}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            cursor: 'pointer',
                                            color: 'primary.main',
                                            textDecoration: 'underline',
                                        }}
                                        onClick={() => {
                                            setShowNewIncomings(!showNewIncomings);
                                            setData([]);
                                        }}
                                    >
                                        {showNewIncomings ? t('showAll', 'Показати все') : t('newIncomings', 'Нові надходження')}
                                    </Typography>
                                </Box>
                            )}
                        </Collection>
                        {pageInfo?.hasNextPage && !loading && (
                            <Box textAlign="center" mt={4}>
                                <Button variant="outlined" onClick={handleLoadMore}>
                                    {t('loadMore', 'Завантажити ще')}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
};

export default Catalogue;
